import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import type {
    IDeleteReturnReason,
    IGetReturnReasons,
    IGetTaskPayrollDetails,
    IReasonCode,
    ITaskPayor,
    ITaskPayrollDetails
} from "../../../../../resources-vue/vue-interfaces/i-task";
import { IPromptOptions } from "../../../../../resources-vue/vue-dialogs/prompt/prompt";
import { TaskService } from "../../../../../services/task.service";
import { ToastrService } from "../../../../../services/toastr.service";
import { PhysiciansService } from "../../../../../services/physician-service";
import { ITypeaheadOptions } from "../../../../../resources-vue/vue-interfaces/i-typeahead";
import { ITaskDetailsPayload } from "../../../../../resources-vue/vue-interfaces/i-task-details";
import type { IEmployeeDetails } from "../../../../../resources-vue/vue-interfaces/i-homecare-reassign";
import {
    ApplicationEnum,
    ConfirmationStatus,
    ConfirmationStatusEnum,
    VisitStatusEnum
} from "../../../../../enums/enums";
import { enumToMap } from "../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { formatDateInput, formatDate } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { IHandleTaskReschuleIssue } from "../../../../../resources-vue/vue-interfaces/i-visit-details";
import { IRescheduleValidations } from "../../../../../resources/dialogs/reschedule-validations/reschedule-validations";

import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import DatePicker from "../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import TimePicker from "../../../../../resources-vue/vue-custom-elements/TimePicker/TimePicker.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import MidnightSplitTable from "../../../../../resources-vue/vue-elements/MidnightSplitTable/MidnightSplitTable.vue";
import PayrollDetailsTable from "../../../../../resources-vue/vue-elements/PayrollDetailsTable/PayrollDetailsTable.vue";
import CommentInput from "../../../../../resources-vue/vue-custom-elements/CommentInput/CommentInput.vue";
import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import RescheduleValidation from "../../../../../resources-vue/vue-dialogs/RescheduleValidations/RescheduleValidation.vue";
import VisitAttachmentsHC from "../VisitAttachmentsHC/VisitAttachmentsHC.vue";

export default defineComponent({
    components: {
        FormSection,
        DatePicker,
        TimePicker,
        TypeaheadInput,
        MidnightSplitTable,
        PayrollDetailsTable,
        CommentInput,
        VisitAttachmentsHC
    },
    inject: ["dialogRef"],

    props: {
        visitDetails: {
            type: Object
        },
        isSaving: {
            type: Boolean,
            default: false
        },
        reassign: {
            type: Function
        },
        cancel: {
            type: Function
        },
        ok: {
            type: Function
        },
        isPlanner: {
            type: Boolean,
            default: false
        }
    },

    data(vm) {
        return {
            payorList: [] as ITaskPayor[],
            _taskService: null,
            _toastrService: null,
            _physicianService: null,
            _dialogRef: this.dialogRef as any,
            commentInputClass: { input: "w-35" } as { input: string; textarea?: string },
            taskDetails: null,
            isDeleteRequested: false as boolean,
            isDeleteInProgress: false as boolean,
            returnReasons: [] as IReasonCode[],
            payrollDetails: [] as ITaskPayrollDetails[],
            isSavingReason: false as boolean,
            showPhyicianField: false as boolean,
            showAssignedToList: false as boolean,
            assignedToName: "" as string,
            scheduledStartTime: "" as string,
            scheduledEndTime: "" as string,
            visitTimeIn: "" as string,
            visitTimeOut: "" as string,
            isepisodeSelectionChecked: false as boolean,
            physician: null,
            hasPayrollDetails: true as boolean,
            payrollDetailsLoading: true as boolean,
            isLoading: false as boolean,
            employeeList: [] as Array<IEmployeeDetails>,
            scheduleEmployee: {} as ITypeaheadOptions,
            selectedTask: {} as ITypeaheadOptions,
            disciplineTasksList: [] as Array<ITypeaheadOptions>,
            confirmationStatusEnum: ConfirmationStatus,
            confirmationStatus: 0,
            formatDateInput,
            formatDate,
            enumToMap
        };
    },

    emits: ["update:isSaving"],

    async created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._physicianService = Container.instance.get(PhysiciansService);
        Object.assign(this.visitDetails, {});
        try {
            this.isLoading = true;
            const payload: ITaskDetailsPayload = {
                application: this.visitDetails.Application,
                agencyId: this.visitDetails.AgencyId,
                eventId: this.visitDetails.Id,
                episodeId: this.visitDetails.EpisodeId,
                patientId: this.visitDetails.PatientId
            };
            this.taskDetails = await this._taskService.getHHVisitDetails(payload);
            this.scheduledStartTime = moment(this.taskDetails.scheduleEvent.eventStartTime).format("hh:mm A");
            this.scheduledEndTime = moment(this.taskDetails.scheduleEvent.eventEndTime).format("hh:mm A");
            this.visitTimeIn = moment(this.taskDetails.scheduleEvent.visitStartTime).format("hh:mm A");
            this.visitTimeOut = moment(this.taskDetails.scheduleEvent.visitEndTime).format("hh:mm A");
            this.assignedToName = this.taskDetails.scheduleEvent.userName;
            if (this.taskDetails.scheduleEvent.isOrder || this.taskDetails.scheduleEvent.sendAsOrder) {
                this.showPhyicianField = true;
                this.physician = {
                    name: this.taskDetails.scheduleEvent.physicianName,
                    value: this.taskDetails.scheduleEvent.physicianId
                };
            }
            if (
                (this.visitDetails.Status === "Reopened" ||
                    this.visitDetails.Status === "Not Yet Started" ||
                    this.visitDetails.Status === "Saved" ||
                    this.visitDetails.Status === "Returned For Review" ||
                    this.visitDetails.Status === "Returned for Clinician Signature") &&
                !this.visitDetails.IsNonVisitAssignment
            ) {
                const employees = await this._taskService.homecareEmployeePreference({
                    PatientId: this.visitDetails.PatientId,
                    AgencyId: this.visitDetails.AgencyId,
                    LocationId: this.visitDetails.LocationId,
                    StartDate: this.visitDetails.StartDate,
                    EndDate: this.visitDetails.EndDate,
                    Application: ApplicationEnum.HomeCare
                });
                this.employeeList = employees;
                this.showAssignedToList = true;
            }
            if (
                this.isPlanner &&
                (this.visitDetails.StatusName === "Reopened" ||
                    this.visitDetails.StatusName === "Not Yet Started" ||
                    this.visitDetails.StatusName === "Saved" ||
                    this.visitDetails.StatusName === "Returned For Review" ||
                    this.visitDetails.StatusName === "Returned for Clinician Signature")
            ) {
                const employees = await this._taskService.homecareEmployeePreference({
                    PatientId: this.visitDetails.PatientId,
                    AgencyId: this.visitDetails.AgencyId,
                    LocationId: this.visitDetails.LocationId,
                    StartDate: this.visitDetails.StartDate,
                    EndDate: this.visitDetails.EndDate,
                    Application: ApplicationEnum.HomeCare
                });
                this.employeeList = employees;
                this.showAssignedToList = true;
            }
            const payors = await this._taskService.getTaskPayorsList({
                patientId: this.visitDetails.PatientId,
                agencyId: this.visitDetails.AgencyId,
                startDate: this.visitDetails.StartDate,
                endDate: this.visitDetails.EndDate,
                application: ApplicationEnum.HomeCare
            });
            this.payorList = payors.data;
            if (this.taskDetails.scheduleEvent.isVisitVerified) {
                this.confirmationStatus = ConfirmationStatusEnum.VisitConfirmed;
            }
            if (this.taskDetails.scheduleEvent.isRescheduleVisit) {
                this.confirmationStatus = ConfirmationStatusEnum.ResceduleVisit;
            }
            if (this.taskDetails.scheduleEvent.userId) {
                const employeeDetails = this.employeeList.find((emp) => {
                    return emp.Id == this.taskDetails.scheduleEvent.userId;
                });
                this.scheduleEmployee = {
                    value: employeeDetails.Id,
                    name: `${employeeDetails.FirstName} ${employeeDetails.LastName}`
                };
            }

            this.disciplineTasksList = this.taskDetails.disciplineTasks?.map((item: any) => {
                return {
                    name: item.text,
                    value: item.value
                };
            });
            if (this.taskDetails.scheduleEvent.customNoteId) {
                const taskDetails = this.disciplineTasksList.find((task) => {
                    return task.value == this.taskDetails.scheduleEvent.customNoteId;
                });
                this.selectedTask = {
                    value: taskDetails.value,
                    name: taskDetails.name
                };
            }

            await this.getReturnReasonList();
            await this.getPayrollDetails();
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    },

    computed: {
        getCarePeriod(): string {
            let startDate = "";
            let endDate = "";
            if (this.taskDetails && this.taskDetails.scheduleEvent) {
                startDate = moment(this.taskDetails.scheduleEvent.startDate).format("MM/DD/YYYY");
                const endDateYear = moment(this.taskDetails.scheduleEvent.endDate).year();
                if (endDateYear == 9999) {
                    endDate = "Open";
                } else {
                    endDate = moment(this.taskDetails.scheduleEvent.endDate).format("MM/DD/YYYY");
                }
            }
            const carePeriod = startDate + " - " + endDate;
            return carePeriod;
        },
        areFieldsEnabled() {
            if (this.visitDetails.IsNonVisitAssignment) {
                return true;
            } else if (this.isPlanner) {
                return (
                    this.visitDetails.StatusName === VisitStatusEnum.NotYetDue ||
                    this.visitDetails.StatusName === VisitStatusEnum.NotStarted
                );
            } else {
                return (
                    this.visitDetails.Status === VisitStatusEnum.NotYetDue ||
                    this.visitDetails.Status === VisitStatusEnum.NotStarted
                );
            }
        }
    },

    watch: {
        confirmationStatus(newValue) {
            if (newValue == ConfirmationStatusEnum.VisitConfirmed) {
                this.taskDetails.scheduleEvent.isVisitVerified = true;
                this.taskDetails.scheduleEvent.isRescheduleVisit = false;
            } else if (newValue == ConfirmationStatusEnum.ResceduleVisit) {
                this.taskDetails.scheduleEvent.isRescheduleVisit = true;
                this.taskDetails.scheduleEvent.isVisitVerified = false;
            } else {
                this.taskDetails.scheduleEvent.isRescheduleVisit = false;
                this.taskDetails.scheduleEvent.isVisitVerified = false;
            }
        },
        scheduledStartTime(newValue) {
            this.scheduledStartTimeChanged(newValue);
        },
        scheduledEndTime(newValue) {
            this.scheduledEndTimeChanged(newValue);
        },
        visitTimeIn(newValue) {
            this.visitTimeInChanged(newValue);
        },
        visitTimeOut(newValue) {
            this.visitTimeOutChanged(newValue);
        },
        scheduleEmployee(newValue: ITypeaheadOptions) {
            if (!!newValue) {
                const employeeDetails = this.employeeList.find((emp) => {
                    return emp.Id == newValue.value;
                });
                if (employeeDetails) {
                    this.taskDetails.scheduleEvent.userId = employeeDetails.Id;
                    this.taskDetails.scheduleEvent.userFirstName = employeeDetails.FirstName;
                    this.taskDetails.scheduleEvent.userLastName = employeeDetails.LastName;
                    this.taskDetails.scheduleEvent.userCredentials = [
                        employeeDetails.Credentials,
                        employeeDetails.CredentialsOther
                    ];
                }
            }
        },
        selectedTask(newValue: ITypeaheadOptions) {
            if (!!newValue) {
                this.taskDetails.scheduleEvent.customNoteId = newValue.value;
            }
        }
    },
    methods: {
        handleDateChange(date: any, name: string) {
            if (name === "eventStartTime") {
                this.taskDetails.scheduleEvent.eventStartTime = date;
            }
            if (name === "eventEndTime") {
                this.taskDetails.scheduleEvent.eventEndTime = date;
            }
            if (name === "visitStartTime") {
                this.taskDetails.scheduleEvent.visitStartTime = date;
            }
            if (name === "visitEndTime") {
                this.taskDetails.scheduleEvent.visitEndTime = date;
            }
        },
        changeVisitTimeOut(time: any) {
            this.visitTimeOut = time;
        },
        changeVisitTimeIn(time: any) {
            this.visitTimeIn = time;
        },
        changeScheduleEndTime(time: any) {
            this.scheduledEndTime = time;
        },
        changeIsAllDay(time: any) {
            this.scheduledStartTime = time;
        },
        resultUpdatedAppended(value: string) {
            this.taskDetails.scheduleEvent.comments += value;
        },
        resultUpdated(value: string) {
            this.taskDetails.scheduleEvent.comments = value;
        },
        isReturnedWithSignature() {
            return this.taskDetails?.scheduleEvent?.scheduleStatus == 135;
        },
        async getPhysicianList(filter: string, limit: number) {
            const data = await this._physicianService.getPhysiciansByName({
                name: filter,
                page: 1,
                pageLength: limit,
                providerIds: [],
                application: this.visitDetails.Application,
                accountId: this.taskDetails.scheduleEvent.agencyId
            });
            return data.items?.map((item: any) => ({ value: item.id, name: item.name }));
        },
        physicianChanged(newValue: any) {
            if (newValue != null) {
                this.taskDetails.scheduleEvent.physicianId = newValue.value;
                this.taskDetails.scheduleEvent.physicianName = newValue.name;
            }
        },

        scheduledStartTimeChanged(newValue: string) {
            if (!!newValue && this.taskDetails) {
                const eventStartTime = moment(this.taskDetails.scheduleEvent.eventStartTime);
                this.taskDetails.scheduleEvent.eventStartTime = this.updateTime(newValue, eventStartTime);
            }
        },

        scheduledEndTimeChanged(newValue: string) {
            if (!!newValue && this.taskDetails) {
                const eventEndTime = moment(this.taskDetails.scheduleEvent.eventEndTime);
                this.taskDetails.scheduleEvent.eventEndTime = this.updateTime(newValue, eventEndTime);
            }
        },

        visitTimeInChanged(newValue: string) {
            if (!!newValue && this.taskDetails) {
                const visitStartTime = moment(this.taskDetails.scheduleEvent.visitStartTime);
                this.taskDetails.scheduleEvent.visitStartTime = this.updateTime(newValue, visitStartTime);
            }
        },

        visitTimeOutChanged(newValue: string) {
            if (!!newValue && this.taskDetails) {
                const visitEndTime = moment(this.taskDetails.scheduleEvent.visitEndTime);
                this.taskDetails.scheduleEvent.visitEndTime = this.updateTime(newValue, visitEndTime);
            }
        },

        updateTime(newTime: string, dateToUpdate: any) {
            const time24hour = moment(newTime, "hh:mm A").format("HH:mm");
            const hour = Number(time24hour.split(":")[0]);
            const minutes = Number(time24hour.split(":")[1]);
            const updatedDate = dateToUpdate
                .startOf("day")
                .set("hour", hour)
                .set("minute", minutes)
                .format("YYYY-MM-DDTHH:mm:ss");
            return updatedDate;
        },

        employeeListFilter(filter: string) {
            if (filter) {
                const filterEmployeeList = this.employeeList.filter((employee) => {
                    const fullName = `${employee.FirstName} ${employee.LastName}`;
                    return fullName.toLowerCase().includes(filter.toLowerCase());
                });
                return filterEmployeeList?.map((item) => {
                    return {
                        name: `${item.FirstName} ${item.LastName}`,
                        value: item.Id
                    };
                });
            } else {
                return this.employeeList?.map((item) => ({
                    value: item.Id,
                    name: `${item.FirstName} ${item.LastName}`
                }));
            }
        },

        changescheduleEmployee(data: any) {
            this.scheduleEmployee = data;
        },

        availableTasksFilter(filter: string) {
            if (filter) {
                const filterAvailableTasks = this.disciplineTasksList.filter((task: any) => {
                    return task.name.toLowerCase().includes(filter.toLowerCase());
                });
                return filterAvailableTasks;
            } else {
                return this.disciplineTasksList;
            }
        },

        changeTaskList(data: any) {
            this.selectedTask = data;
        },
        selectedPayor(payorId: string) {
            if (!!payorId) {
                const payorDetails = this.payorList.find((emp) => {
                    return emp.insuranceId == payorId;
                });
                if (payorDetails) {
                    this.taskDetails.scheduleEvent.payorId = payorDetails.insuranceId;
                    this.taskDetails.scheduleEvent.payer = payorDetails.name;
                }
            }
        },

        confirmDeleteReason() {
            this.isDeleteRequested = true;
        },

        cancelDeleteReason() {
            this.isDeleteRequested = false;
        },

        async deleteReason(id: number) {
            try {
                this.isDeleteInProgress = true;
                const deleteQuery: IDeleteReturnReason = {
                    application: this.visitDetails.Application,
                    id: id
                };
                await this._taskService.deleteReturnReasonHC(deleteQuery);
                this.getReturnReasonList();
                this._toastrService.success({
                    message: `Return Reason has been successfully deleted.`,
                    title: `Return Reason Deleted`
                });
            } catch (error) {
                console.log(error);
                this._toastrService.error({
                    title: `Error - Delete Return Reason`,
                    message: `There was a problem while deleting the Return Reason. Please try again.`
                });
                this.isDeleteInProgress = false;
            }
        },

        async getReturnReasonList() {
            const payload: IGetReturnReasons = {
                application: this.visitDetails.Application,
                agencyId: this.visitDetails.AgencyId,
                eventId: this.visitDetails.Id,
                episodeId: this.visitDetails.EpisodeId,
                patientId: this.visitDetails.PatientId,
                userId: this.visitDetails.UserId
            };
            const returnReasons = await this._taskService.getReturnReasonList(payload);
            this.returnReasons = returnReasons.data;
        },

        async getPayrollDetails() {
            this.payrollDetailsLoading = true;
            const payload: IGetTaskPayrollDetails = {
                application: this.visitDetails.Application,
                agencyId: this.visitDetails.AgencyId,
                taskId: this.taskDetails.scheduleEvent.eventId
            };
            const returnReasons = await this._taskService.getTaskPayrollDetails(payload);
            this.payrollDetails = returnReasons.data;
            if (returnReasons.isSuccess == false) {
                this.hasPayrollDetails = false;
            }
            this.payrollDetailsLoading = false;
        },

        async addReturnReason() {
            // TODO - To be done later
            // const payload: IGetReturnReasons = {
            //     application: this.visitDetails.Application,
            //     taskId: this.taskDetails.scheduleEvent.eventId
            // };
            // this.returnReasons = await this._taskService.getReturnReasonList(payload);
        },

        async updateVisitDetails() {
            if (!!this.taskDetails) {
                try {
                    this.$emit("update:isSaving", true);
                    this.taskDetails.scheduleEvent.application = this.visitDetails.Application;
                    this.taskDetails.scheduleEvent.eventDate = this.taskDetails.scheduleEvent.eventDate
                        ? moment(this.taskDetails.scheduleEvent.eventDate).format("MM/DD/YYYY")
                        : null;
                    this.taskDetails.scheduleEvent.visitDate = this.taskDetails.scheduleEvent.visitDate
                        ? moment(this.taskDetails.scheduleEvent.visitDate).format("MM/DD/YYYY")
                        : null;
                    let response = await this._taskService.updateHHVisitDetails(this.taskDetails.scheduleEvent);
                    if (!response.isSuccessful && response.errorDetails) {
                        // if no error list, then simply show the error and end
                        if (!response.errorDetails.taskIssues.length) {
                            this._toastrService.error({
                                title: `Visit Update Failed`,
                                message: response.errorMessage
                            });
                            throw new Error(response.errorMessage);
                        }

                        const validationList: IRescheduleValidations[] = response.errorDetails.taskIssues.map(
                            (item: any) => {
                                return {
                                    title: item.title as string,
                                    description: [item.message] as string[],
                                    taskIssueSurpressKey: item.taskIssueSurpressKey as string
                                };
                            }
                        );
                        const hasError = response.errorDetails.allTasksHaveErrors;

                        this._dialogRef = this.$dialog.open(RescheduleValidation, {
                            props: {
                                modal: true,
                                closeOnEscape: false,
                                style: {
                                    width: "60vw"
                                },
                                showHeader: false
                            },

                            data: {
                                validations: validationList,
                                name: "Planner",
                                isPlanner: true,
                                component: "Schedule-visit",
                                hasError
                            },

                            onClose: async (options) => {
                                if (options.data === "cancel") {
                                    this.cancel();
                                    return;
                                } else {
                                    const issuesResponseObj: IHandleTaskReschuleIssue = {};
                                    for (let issue of validationList) {
                                        issuesResponseObj[issue.taskIssueSurpressKey] = "ScheduleAllAnyways";
                                    }

                                    this.taskDetails.HandleTaskIssues = issuesResponseObj;
                                    const payload = {
                                        ...this.taskDetails.scheduleEvent,
                                        HandleTaskIssues: this.taskDetails.HandleTaskIssues
                                    };
                                    const secondResponse = await this._taskService.updateHHVisitDetails(payload);
                                    if (!secondResponse.isSuccessful) {
                                        this._toastrService.error({
                                            title: `Visit Update Failed`,
                                            message: response.errorMessage
                                        });
                                        throw new Error(secondResponse.errorMessage);
                                    }
                                    this._toastrService.success({
                                        title: this.visitDetails.IsNonVisitAssignment
                                            ? "Non-Visit Task Updated"
                                            : "Visit Updated",
                                        message: this.visitDetails.IsNonVisitAssignment
                                            ? "The Task has been successfully Updated"
                                            : "Visit has been successfully Updated"
                                    });
                                    this.ok();
                                }
                            }
                        });
                    } else {
                        this._toastrService.success({
                            title: this.visitDetails.IsNonVisitAssignment ? "Non-Visit Task Updated" : "Visit Updated",
                            message: this.visitDetails.IsNonVisitAssignment
                                ? "The Task has been successfully Updated"
                                : "Visit has been successfully Updated"
                        });
                        this.ok();
                    }
                } catch (e) {
                    this._toastrService.error({
                        title: this.visitDetails.IsNonVisitAssignment
                            ? "Non-Visit Task Update Failed"
                            : "Visit Update Failed",
                        message: this.visitDetails.IsNonVisitAssignment
                            ? "Updating Task Operation Failed, Please Contact Axxess if issue still persists."
                            : "Updating Visit Operation Failed, Please Contact Axxess if issue still persists."
                    });
                } finally {
                    this.$emit("update:isSaving", false);
                }
            }
        },

        closeDialog() {
            const promptOptions: IPromptOptions = {
                message: "Are you sure you want to leave the page? Please make sure you saved your work.",
                okText: "Yes, I'm Sure",
                cancelText: "Never Mind"
            };

            this._dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: (options) => {
                    if (options.data === "success") {
                        this.cancel();
                    }
                }
            });
        }
    }
});
